/**
 * ELF-CIRCLE — Cultural Intelligence & Strategy.
 *
 * Section 10 of the company overview is blunt about why this page exists:
 * "we get it because we've lived it" is true, but it does not win an RFP scored
 * against agencies with formal methodologies. So the practice is named, placed
 * *above* Marketing and Web Development rather than beside them, and written as
 * four repeatable stages instead of a founder's biography.
 *
 * Signature mechanism: the feed. Selecting a stage lights the two connectors
 * running down into the discipline cards and swaps what each one receives —
 * the page argues the practice feeds both disciplines by showing the hand-off,
 * not by asserting it in a sentence.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfCultureStage from '../ElfCultureStage';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import streetCulture from '../../../../../assets/elf/street-culture.png';

interface Stage {
  num: string;
  name: string;
  line: string;
  body: string;
  /** What this stage hands down to each discipline. */
  toMarketing: string;
  toWebDev: string;
}

const STAGES: Stage[] = [
  {
    num: '01',
    name: 'Presence',
    line: 'Be in the room before the brief',
    body:
      'Thirty-five years of showing up — venues, scenes, communities — is the input layer. Presence is not networking; it is standing where a signal appears months before it reaches a dashboard.',
    toMarketing: 'Grassroots activation targets chosen from rooms we already sit in, not from a lookalike audience.',
    toWebDev: 'Information architecture built around how a community actually self-identifies, not a generic funnel.',
  },
  {
    num: '02',
    name: 'Signal',
    line: 'Separate what moves from what trends',
    body:
      'A trend is visible to everyone at once and worth very little by the time it is. Signal is the earlier, quieter thing: who shows up, who they bring, what makes them stay, and what makes them tell someone else.',
    toMarketing: 'Media weight placed against resonance rather than raw reach — the difference between noise and compounding.',
    toWebDev: 'Content and page priorities set by what an audience returns for, measured rather than assumed.',
  },
  {
    num: '03',
    name: 'Translation',
    line: 'Turn the read into a decision',
    body:
      'The step most cultural insight never survives. A read becomes a media plan, a rollout calendar, a site architecture and a GEO strategy — one document, both disciplines, no translation layer between vendors.',
    toMarketing: 'A rollout calendar where the release date is a cultural moment, not a diary entry.',
    toWebDev: 'A build spec and GEO plan derived from the same read, so the site answers what people actually ask.',
  },
  {
    num: '04',
    name: 'Compounding',
    line: 'Make the relationship the asset',
    body:
      'ROI is not a report; it is a relationship that compounds. Every engagement is scoped so the audience it builds stays legible and reusable afterwards, in the client’s own systems.',
    toMarketing: 'Owned audience and community relationships that outlive the campaign that created them.',
    toWebDev: 'First-party data captured cleanly at the source, so the next campaign starts ahead of the last one.',
  },
];

export const CulturalIntelligence: React.FC = () => {
  const consult = useConsult();
  const [at, setAt] = useState(0);
  const stage = STAGES[at];

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/cultural-intelligence" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb
              trail={[{ label: 'Home', to: '/' }, { label: 'Services', to: '/services' }, 'Cultural Intelligence']}
            />
          </div>

          {/* --- the practice, named --- */}
          <div className="ci-head">
            <div className="ci-head__copy">
              <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                <i />
                A named practice, not a founder’s biography
              </span>
              <h1 className="elf-page__h1">
                Cultural Intelligence
                <br />
                <span style={{ color: 'var(--acc-panel)' }}>&amp; Strategy.</span>
              </h1>
              <p className="wbody" style={{ maxWidth: '64ch' }}>
                Three decades of grassroots instinct, formalised into something repeatable and
                sellable — a practice that sits above Marketing and Web Development and feeds both,
                rather than a sensibility that shows up in the work by accident.
              </p>
              <div className="ci-head__stats">
                {[
                  ['Above', 'Not beside the disciplines'],
                  ['04', 'Stages, every engagement'],
                  ['01', 'Read — two outputs'],
                ].map(([n, l]) => (
                  <div className="statx" key={l}>
                    <span className="statn">{n}</span>
                    <span className="statl">{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <ElfCultureStage />
          </div>

          {/* --- signature: the feed --- */}
          <div className="ci-feed">
            <div className="ci-feed__head">
              <span className="colhead">The method</span>
              <h2 className="elf-page__h2">One read, two outputs</h2>
            </div>

            <div className="ci-stack" role="tablist" aria-label="Cultural Intelligence stages">
              {STAGES.map((s, i) => (
                <button
                  key={s.num}
                  type="button"
                  role="tab"
                  aria-selected={i === at}
                  className="ci-stage"
                  onClick={() => setAt(i)}
                  onMouseEnter={() => setAt(i)}
                >
                  <span className="wnum">{s.num}</span>
                  <span className="pillarname">{s.name}</span>
                  <span className="wtag" style={{ fontSize: 13 }}>
                    {s.line}
                  </span>
                  <span className="wbar" />
                </button>
              ))}
            </div>

            <p className="ci-body wbody">{stage.body}</p>

            {/* Connectors — they carry the selected stage's number down into
                each discipline so the hand-off is visible, not implied. */}
            <div className="ci-wires" aria-hidden="true">
              <span className="ci-wire">
                <i data-at={at} />
              </span>
              <span className="ci-wire">
                <i data-at={at} />
              </span>
            </div>

            <div className="ci-disciplines">
              {[
                ['Marketing', stage.toMarketing, '/services'],
                ['Web Development', stage.toWebDev, '/services'],
              ].map(([name, receives, to]) => (
                <div className="disc ci-disc" key={name}>
                  <span className="statl">Receives from {stage.name}</span>
                  <span className="stagehead">{name}</span>
                  <p className="wbody" style={{ fontSize: 15 }} key={`${name}-${at}`}>
                    {receives}
                  </p>
                  <Link className="pillghost" to={route(to)} style={{ alignSelf: 'flex-start' }}>
                    The discipline →
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* --- what it changes in an RFP --- */}
          <div className="ci-rfp">
            <span className="ci-rfp__art">
              <img src={streetCulture} alt="" />
              <span />
            </span>
            <div className="ci-rfp__copy">
              <span className="colhead">Why it is named</span>
              <h2 className="elf-page__h2" style={{ marginTop: 10 }}>
                Scored, not assumed.
              </h2>
              <p className="wbody" style={{ maxWidth: '56ch', marginTop: 14 }}>
                RFPs are scored against methodology. An agency that can name its insight layer,
                describe its stages and show what each one hands to the build team is answering a
                question its competitors leave to a biography slide. That is the entire reason this
                practice has a page instead of a paragraph.
              </p>
              <button
                type="button"
                className="pillcta mag"
                style={{ marginTop: 22, alignSelf: 'flex-start' }}
                onClick={() =>
                  consult?.open({
                    source: 'cultural-intelligence.brief',
                    interest: 'Cultural Intelligence',
                    title: 'Brief the Cultural Intelligence practice',
                  })
                }
              >
                Brief the practice <span>→</span>
              </button>
            </div>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/why-elf-circle')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Pillar 02</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  The competitive case
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Where this practice sits against the top five.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <Link className="nextlink" to={route('/ai-and-geo')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">The connective layer</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  AI &amp; GEO
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Where both disciplines meet the algorithm.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default CulturalIntelligence;
